import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, CircularProgress, IconButton, Stack,
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Alert, Avatar, Divider, Drawer, Menu, MenuItem,
    ListItemIcon, DialogContentText, Snackbar, Tabs, Tab, Chip, Collapse,
    FormGroup, FormControlLabel, Checkbox, useTheme, Select
} from '@mui/material';
import { format } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import attendanceService from '../services/attendance.service';
import employeeService from '../services/employee.service';
import workplaceService from '../services/workplace.service';
import dayPlanService from '../services/dayPlan.service';
import AddEditRecordDialog from './AddEditRecordDialog';
import { usePreferences } from '../contexts/PreferencesContext';
import { EmployeeInsightsDetail } from './EmployeeCardInsights';
import SegmentedToggle from './dashboard/SegmentedToggle';

import NfcIcon from '@mui/icons-material/Nfc';
import QrCodeIcon from '@mui/icons-material/QrCode';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { Link as RouterLink } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import LogoutIcon from '@mui/icons-material/Logout';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';

const getInitials = (name) => {
    if (!name) return '';
    const nameParts = name.trim().split(' ');
    if (nameParts.length > 1) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    } else if (nameParts[0] && nameParts[0].length > 1) {
        return nameParts[0].substring(0, 2).toUpperCase();
    }
    return (name[0] || '').toUpperCase();
};

const formatDuration = (ms) => {
    if (typeof ms !== 'number' || isNaN(ms) || ms < 0) return null;
    if (ms === 0) return "0m";

    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.join(' ');
};

const TimelineEventIcon = ({ event }) => {
    // Pastel color palette for icons
    const colors = {
        start: { main: '#C8E6C9', contrast: '#388E3C' }, // Pastel Green
        end: { main: '#FFCDD2', contrast: '#D32F2F' },   // Pastel Red
        break: { main: '#FFF9C4', contrast: '#FBC02D' }, // Pastel Yellow
        status: { main: '#D1C4E9', contrast: '#512DA8' }, // Pastel Purple
        edit: { main: '#F5F5F5', contrast: '#616161' }    // Grey
    };

    let selectedColor;
    let IconComponent;

    switch (event.type) {
        case 'Príchod':
        case 'Arrival':
            selectedColor = colors.start;
            IconComponent = event.scan_type === 'nfc' ? NfcIcon : QrCodeIcon;
            break;
        case 'Odchod':
        case 'Departure':
            selectedColor = colors.end;
            IconComponent = event.scan_type === 'nfc' ? NfcIcon : QrCodeIcon;
            break;
        case 'Začiatok prestávky':
        case 'Break Start':
        case 'Koniec prestávky':
        case 'Break End':
            selectedColor = colors.break;
            IconComponent = FreeBreakfastIcon;
            break;
        case 'Status Start':
        case 'Status End':
            selectedColor = colors.status;
            IconComponent = WorkHistoryIcon;
            break;
        default:
            selectedColor = colors.edit;
            IconComponent = EditIcon;
            break;
    }

    return (
        <Avatar sx={{ bgcolor: selectedColor.main, width: 48, height: 48 }}>
            <IconComponent sx={{ color: selectedColor.contrast }} />
        </Avatar>
    );
};

const formatProfileDate = (value) => {
    if (!value) return '—';
    return format(new Date(value), 'd. M. yyyy');
};

const formatNameDayLabel = (month, day) => {
    if (!month || !day) return '—';
    return format(new Date(2000, month - 1, day), 'd. M.');
};

const isNameDayToday = (month, day) => {
    if (!month || !day) return false;
    const now = new Date();
    return month === now.getMonth() + 1 && day === now.getDate();
};

const EmployeeHistoryDrawer = ({
    employee,
    open,
    onClose,
    onEmployeeDeleted,
    onEmployeeUpdated,
    activeTab = 'timeline',
    selectedDate: initialSelectedDate,
    onRecordUpdate,
    insightsPeriod = 30,
    onInsightsPeriodChange,
    insightsByEmployeeId = {},
    insightsMeta = null,
}) => {
    const theme = useTheme();
    const { t } = usePreferences();
    
    const modalTextFieldStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
            },
        },
        '& label.Mui-focused': {
            color: theme.palette.primary.main,
        },
    };
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(initialSelectedDate || dayjs());

    const [editingEventId, setEditingEventId] = useState(null);
    const [editingTime, setEditingTime] = useState(null);
    const [editingWorkplaceId, setEditingWorkplaceId] = useState('');
    const [editError, setEditError] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isEditEmployeeModalOpen, setEditEmployeeModalOpen] = useState(false);

    const [editingEmployeeData, setEditingEmployeeData] = useState(null);
    // Meniny sa posielajú len keď ich admin naozaj potvrdil v pickeri.
    const [nameDayTouched, setNameDayTouched] = useState(false);
    const [notificationData, setNotificationData] = useState({ title: 'Admin', body: '' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [currentTab, setCurrentTab] = useState(activeTab);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // New state for workplace management
    const [workplaces, setWorkplaces] = useState([]);
    const [assignedWorkplaces, setAssignedWorkplaces] = useState(new Set());
    const [workplacesLoading, setWorkplacesLoading] = useState(false);

    // Workplaces the employee is actually assigned to (for picking one on a record)
    const assignedWorkplaceOptions = workplaces
        .filter(wp => assignedWorkplaces.has(wp.id))
        .map(wp => ({ id: wp.id, name: wp.name }));

    // State for add record dialog
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    // State for day plan (plánovač)
    const [dayPlan, setDayPlan] = useState(null);
    const [dayPlanLoading, setDayPlanLoading] = useState(false);
    const [insightDetail, setInsightDetail] = useState(null);
    const [insightLoading, setInsightLoading] = useState(false);

    const cachedInsight = employee ? insightsByEmployeeId[String(employee.id)] : null;

    const insightsPeriodLabel = (() => {
        const labelKey = insightsPeriod === 90 ? 'period90' : insightsPeriod === 365 ? 'period365' : 'period30';
        const periodName = t(`pages.employees.insights.${labelKey}`);
        let label = t('pages.employees.insights.periodLabel').replace('{period}', periodName);
        if (insightsMeta?.rangeFrom && insightsMeta?.rangeTo) {
            label += ` (${insightsMeta.rangeFrom} – ${insightsMeta.rangeTo})`;
        }
        return label;
    })();

    const historyDays = history.map(record => dayjs(record.start_time).format('YYYY-MM-DD'));
    const selectedDayRecords = history.filter(record =>
        dayjs(record.start_time).isSame(selectedDate, 'day')
    );
    const activeRecord = selectedDayRecords.find(record => !record.end_time);
    const activeBreak = selectedDayRecords.flatMap(record => record.breaks || [])
        .find(breakRecord => !breakRecord.break_end_time);
    const activeStatus = selectedDayRecords.flatMap(record => record.statuses || [])
        .find(statusRecord => !statusRecord.end_time);

    useEffect(() => {
        if (employee) {
            setEditingEmployeeData({ ...employee });
            setNameDayTouched(false);
            setNotificationData({ title: 'Admin', body: '' });
        }
    }, [employee]);

    // Close edit panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (editingEventId && !e.target.closest('.timeline-item-container') && !e.target.closest('.MuiCollapse-root')) {
                handleCancelEdit();
            }
        };

        if (editingEventId) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [editingEventId]);

    useEffect(() => {
        if (open && employee) {
            fetchWorkplaces();
            fetchAssignedWorkplaces();
        }
    }, [open, employee]);

    useEffect(() => {
        if (!open || !employee) {
            setInsightDetail(null);
            return;
        }

        if (cachedInsight) {
            setInsightDetail(cachedInsight);
            setInsightLoading(false);
            return;
        }

        setInsightLoading(true);
        employeeService.getEmployeeInsights(employee.id, insightsPeriod)
            .then((res) => {
                setInsightDetail({
                    devices: res.data?.devices || [],
                    hasDevices: res.data?.hasDevices,
                    averages: res.data?.averages,
                });
            })
            .catch(() => setInsightDetail(null))
            .finally(() => setInsightLoading(false));
    }, [open, employee, insightsPeriod, cachedInsight]);

    useEffect(() => {
        if (open) {
            setCurrentTab(activeTab);
        }
    }, [activeTab, open]);

    useEffect(() => {
        if (initialSelectedDate) {
            setSelectedDate(initialSelectedDate);
        }
    }, [initialSelectedDate, open, activeTab]);

    // Fetch day plan when employee + selectedDate change (timeline or plan tab)
    useEffect(() => {
        if (!open || !employee || (currentTab !== 'timeline' && currentTab !== 'plan')) {
            setDayPlan(null);
            return;
        }
        const dateStr = selectedDate.format('YYYY-MM-DD');
        setDayPlanLoading(true);
        setDayPlan(null);
        dayPlanService.getEmployeeDayPlan(employee.id, dateStr)
            .then((res) => {
                setDayPlan(res.data?.plan || null);
            })
            .catch(() => setDayPlan(null))
            .finally(() => setDayPlanLoading(false));
    }, [open, employee, selectedDate, currentTab]);

    const handleTabChange = (event, newValue) => {
        if (newValue === 'notification' && !employee?.has_fcm_token) {
            setSnackbar({
                open: true,
                message: 'This employee has not enabled notifications in the app.',
                severity: 'warning',
            });
            return;
        }
        setCurrentTab(newValue);
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenEditEmployeeModal = () => {
        setNameDayTouched(false);
        setEditEmployeeModalOpen(true);
        handleMenuClose();
        // Load current workplace assignments when opening edit modal
        if (employee) {
            fetchAssignedWorkplaces();
        }
    };

    const handleCloseEditEmployeeModal = () => {
        setNameDayTouched(false);
        setEditEmployeeModalOpen(false);
        // Reset workplace assignments to original state
        if (employee) {
            fetchAssignedWorkplaces();
        }
    };

    const handleEditEmployeeSubmit = async () => {
        if (!editingEmployeeData) return;
        try {
            const dataToSubmit = {
                name: editingEmployeeData.name,
                position: editingEmployeeData.position,
                email: editingEmployeeData.email,
                phone_number: editingEmployeeData.phone_number,
                birthday: editingEmployeeData.birthday,
                employment_start_date: editingEmployeeData.employment_start_date,
            };

            // Meniny sa inak odvodzujú z mena podľa kalendára, preto ich posielame
            // len vtedy, keď admin dátum naozaj potvrdil alebo vymazal.
            if (nameDayTouched) {
                if (editingEmployeeData.name_day_month && editingEmployeeData.name_day_day) {
                    dataToSubmit.name_day_month = editingEmployeeData.name_day_month;
                    dataToSubmit.name_day_day = editingEmployeeData.name_day_day;
                } else {
                    dataToSubmit.clear_name_day = true;
                }
            }

            const updatedEmployee = await employeeService.updateEmployee(editingEmployeeData.id, dataToSubmit);
            
            // Update workplace assignments
            await employeeService.assignWorkplaces(editingEmployeeData.id, Array.from(assignedWorkplaces));
            
            if (onEmployeeUpdated) {
                onEmployeeUpdated(updatedEmployee.data);
            }
            handleCloseEditEmployeeModal();
            setSnackbar({ open: true, message: 'Employee updated successfully.', severity: 'success' });
            
            // Refresh workplace data
            fetchWorkplaces();
            fetchAssignedWorkplaces();
        } catch (error) {
            console.error("Failed to update employee:", error);
            setSnackbar({ open: true, message: 'Failed to update employee.', severity: 'error' });
        }
    };

    const handleDeleteEmployee = async () => {
        if (!employee) return;
        try {
            await employeeService.deleteEmployee(employee.id);
            if (onEmployeeDeleted) {
                onEmployeeDeleted(employee.id);
            }
            onClose();
        } catch (error) {
            console.error("Failed to delete employee:", error);
            setSnackbar({ open: true, message: 'Failed to delete employee.', severity: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            handleMenuClose();
        }
    };

    const handleSendNotification = async () => {
        if (!employee) return;

        try {
            await employeeService.sendNotification(employee.id, notificationData.title, notificationData.body);
            setNotificationData({ title: 'Admin', body: '' });
            setSnackbar({ open: true, message: 'Notification sent successfully.', severity: 'success' });
        } catch (error) {
            console.error("Failed to send notification:", error);
            const errorMessage = error.response?.data?.message === "Employee does not have a registered FCM token."
                ? "Employee does not have active notifications."
                : "Failed to send notification.";
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    useEffect(() => {
        const fetchAndSetHistory = async () => {
            if (!employee) return;
            setLoading(true);
            try {
                const historyRes = await attendanceService.getEmployeeHistory(employee.id);
                setHistory(historyRes.data || []);
            } catch (error) {
                console.error("Failed to fetch history:", error);
                setHistory([]);
            } finally {
                setLoading(false);
            }
        };

        if (open && employee) {
            fetchAndSetHistory();
        }
        if (!open) {
            setSelectedDate(initialSelectedDate || dayjs());
            setCurrentTab(activeTab);
        }
    }, [open, employee, initialSelectedDate, activeTab]);

    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
    };

    const handleOpenEdit = (event) => {
        // Toggle: if clicking on the same item, close it; otherwise open the new one
        if (editingEventId === event.id) {
            setEditingEventId(null);
            setEditingTime(null);
            setEditingWorkplaceId('');
            setEditError(null);
        } else {
            setEditingEventId(event.id);
            setEditingTime(dayjs(event.time));
            setEditingWorkplaceId(event.record?.workplace_id ?? '');
            setEditError(null);
        }
    };

    const handleCancelEdit = () => {
        setEditingEventId(null);
        setEditingTime(null);
        setEditingWorkplaceId('');
        setEditError(null);
    };

    // Close edit panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (editingEventId && !e.target.closest('.timeline-item-container') && !e.target.closest('.MuiCollapse-root')) {
                handleCancelEdit();
            }
        };

        if (editingEventId) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [editingEventId]);


    const handleEditAttendanceSubmit = async () => {
        if (!editingEventId) return;

        const eventToEdit = timelineEvents.find(e => e.id === editingEventId);
        if (!eventToEdit) return;

        let recordType, dataToSend, recordId;

        setEditError(null);

        switch (eventToEdit.type) {
            case 'Príchod':
            case 'Odchod': {
                recordType = 'shift';
                recordId = eventToEdit.record.id;

                const originalStartTime = dayjs(eventToEdit.record.start_time);
                const originalEndTime = eventToEdit.record.end_time ? dayjs(eventToEdit.record.end_time) : null;

                const newStartTime = eventToEdit.type === 'Príchod' ? editingTime : originalStartTime;
                const newEndTime = eventToEdit.type === 'Odchod' ? editingTime : originalEndTime;

                if (newEndTime && newStartTime.isAfter(newEndTime)) {
                    setEditError("Začiatok zmeny nemôže byť nastavený po jej ukončení.");
                    return;
                }

                dataToSend = {
                    ...eventToEdit.record,
                    start_time: newStartTime.format('YYYY-MM-DD HH:mm:ss'),
                    end_time: newEndTime ? newEndTime.format('YYYY-MM-DD HH:mm:ss') : null,
                    scan_type_start: 'edit',
                    scan_type_end: 'edit',
                };
                if (editingWorkplaceId) {
                    dataToSend.workplace_id = editingWorkplaceId;
                }
                delete dataToSend.breaks;
                delete dataToSend.statuses;
                delete dataToSend.workplace_name;
                break;
            }
            case 'Začiatok prestávky':
            case 'Koniec prestávky': {
                recordType = 'break';
                recordId = eventToEdit.breakRecord.id;

                const originalBreakStartTime = dayjs(eventToEdit.breakRecord.break_start_time);
                const originalBreakEndTime = eventToEdit.breakRecord.break_end_time ? dayjs(eventToEdit.breakRecord.break_end_time) : null;

                const newBreakStartTime = eventToEdit.type === 'Začiatok prestávky' ? editingTime : originalBreakStartTime;
                const newBreakEndTime = eventToEdit.type === 'Koniec prestávky' ? editingTime : originalBreakEndTime;

                if (newBreakEndTime && newBreakStartTime.isAfter(newBreakEndTime)) {
                    setEditError("Začiatok prestávky nemôže byť nastavený po jej ukončení.");
                    return;
                }

                dataToSend = {
                    break_start_time: newBreakStartTime.format('YYYY-MM-DD HH:mm:ss'),
                    break_end_time: newBreakEndTime ? newBreakEndTime.format('YYYY-MM-DD HH:mm:ss') : null,
                };
                break;
            }
            case 'Status Start':
            case 'Status End': {
                recordType = 'status';
                recordId = eventToEdit.statusRecord.id;

                const originalStatusStartTime = dayjs(eventToEdit.statusRecord.start_time);
                const originalStatusEndTime = eventToEdit.statusRecord.end_time ? dayjs(eventToEdit.statusRecord.end_time) : null;

                const newStatusStartTime = eventToEdit.type === 'Status Start' ? editingTime : originalStatusStartTime;
                const newStatusEndTime = eventToEdit.type === 'Status End' ? editingTime : originalStatusEndTime;

                if (newStatusEndTime && newStatusStartTime.isAfter(newStatusEndTime)) {
                    setEditError("Začiatok statusu nemôže byť nastavený po jeho ukončení.");
                    return;
                }

                dataToSend = {
                    start_time: newStatusStartTime.format('YYYY-MM-DD HH:mm:ss'),
                    end_time: newStatusEndTime ? newStatusEndTime.format('YYYY-MM-DD HH:mm:ss') : null,
                };
                break;
            }
            default:
                return;
        }

        try {
            await attendanceService.updateAttendanceRecord(recordId, recordType, dataToSend);
            const historyRes = await attendanceService.getEmployeeHistory(employee.id);
            setHistory(historyRes.data || []);
            handleCancelEdit();
            setSnackbar({ open: true, message: 'Record updated successfully.', severity: 'success' });
            if (onRecordUpdate) onRecordUpdate();
        } catch (error) {
            console.error(`Failed to update ${recordType}:`, error);
            setEditError(`Failed to update ${recordType}. Please check the times and try again.`);
            setSnackbar({ open: true, message: `Failed to update ${recordType}.`, severity: 'error' });
        }
    };

    const handleEndShift = async () => {
        if (!activeRecord) return;
        
        try {
            // If there's an active break, end it first
            if (activeBreak) {
                const breakDataToSend = { break_end_time: dayjs().format('YYYY-MM-DD HH:mm:ss') };
                await attendanceService.updateAttendanceRecord(activeBreak.id, 'break', breakDataToSend);
            }
            
            // Then end the shift
            const dataToSend = {
                end_time: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                scan_type_end: 'manual'
            };
            await attendanceService.updateAttendanceRecord(activeRecord.id, 'shift', dataToSend);
            
            const historyRes = await attendanceService.getEmployeeHistory(employee.id);
            setHistory(historyRes.data || []);
            setSnackbar({ 
                open: true, 
                message: activeBreak 
                    ? 'Prestávka a zmena úspešne ukončené.' 
                    : 'Zmena úspešne ukončená.', 
                severity: 'success' 
            });
        } catch (error) {
            console.error("Failed to end shift:", error);
            setSnackbar({ open: true, message: 'Nepodarilo sa ukončiť zmenu.', severity: 'error' });
        }
    };

    const handleEndActiveBreak = async () => {
        if (!activeBreak) return;
        const dataToSend = { break_end_time: dayjs().format('YYYY-MM-DD HH:mm:ss') };
        try {
            await attendanceService.updateAttendanceRecord(activeBreak.id, 'break', dataToSend);
            const historyRes = await attendanceService.getEmployeeHistory(employee.id);
            setHistory(historyRes.data || []);
            setSnackbar({ open: true, message: 'Prestávka úspešne ukončená.', severity: 'success' });
        } catch (error) {
            console.error("Failed to end break:", error);
            setSnackbar({ open: true, message: 'Nepodarilo sa ukončiť prestávku.', severity: 'error' });
        }
    };

    const handleEndActiveStatus = async () => {
        if (!activeStatus) return;
        const dataToSend = { end_time: dayjs().format('YYYY-MM-DD HH:mm:ss') };
        try {
            await attendanceService.updateAttendanceRecord(activeStatus.id, 'status', dataToSend);
            const historyRes = await attendanceService.getEmployeeHistory(employee.id);
            setHistory(historyRes.data || []);
            setSnackbar({ open: true, message: 'Status úspešne ukončený.', severity: 'success' });
        } catch (error) {
            console.error("Failed to end status:", error);
            setSnackbar({ open: true, message: 'Nepodarilo sa ukončiť status.', severity: 'error' });
        }
    };


    let totalWorkDurationMs = 0;
    let totalBreakDurationMs = 0;
    let totalBruttoDurationMs = 0;

    // Calculate totals from all records for the selected day
    selectedDayRecords.forEach(record => {
        const recordBreakDurationMs = record.breaks?.reduce((acc, br) => {
            if (br.break_start_time && br.break_end_time) {
                return acc + (new Date(br.break_end_time).getTime() - new Date(br.break_start_time).getTime());
            }
            return acc;
        }, 0) || 0;
        totalBreakDurationMs += recordBreakDurationMs;

        if (record.end_time) {
            const totalDurationMs = new Date(record.end_time).getTime() - new Date(record.start_time).getTime();
            totalBruttoDurationMs += totalDurationMs;
            totalWorkDurationMs += (totalDurationMs - recordBreakDurationMs);
        }
    });

    const timelineEvents = [];
    selectedDayRecords.forEach(record => {
        timelineEvents.push({
            time: record.start_time,
            type: 'Príchod',
            id: `start-${record.id}`,
            scan_type: record.scan_type_start,
            recordId: record.id,
            record: record
        });
        if (record.breaks) {
            record.breaks.forEach(b => {
                timelineEvents.push({
                    time: b.break_start_time,
                    type: 'Začiatok prestávky',
                    id: `break-start-${b.id}`,
                    recordId: record.id,
                    record: record,
                    breakRecord: b
                });
                if (b.break_end_time) {
                    const breakDurationMs = new Date(b.break_end_time).getTime() - new Date(b.break_start_time).getTime();
                    timelineEvents.push({
                        time: b.break_end_time,
                        type: 'Koniec prestávky',
                        id: `break-end-${b.id}`,
                        duration: formatDuration(breakDurationMs),
                        recordId: record.id,
                        record: record,
                        breakRecord: b
                    });
                }
            });
        }
        if (record.statuses) {
            record.statuses.forEach(s => {
                timelineEvents.push({
                    time: s.start_time,
                    type: 'Status Start',
                    id: `status-start-${s.id}`,
                    name: s.status_name,
                    recordId: record.id,
                    record: record,
                    statusRecord: s
                });
                if (s.end_time) {
                    const statusDurationMs = new Date(s.end_time).getTime() - new Date(s.start_time).getTime();
                    timelineEvents.push({
                        time: s.end_time,
                        type: 'Status End',
                        id: `status-end-${s.id}`,
                        name: s.status_name,
                        duration: formatDuration(statusDurationMs),
                        recordId: record.id,
                        record: record,
                        statusRecord: s
                    });
                }
            });
        }
        if (record.end_time) {
            timelineEvents.push({
                time: record.end_time,
                type: 'Odchod',
                id: `end-${record.id}`,
                scan_type: record.scan_type_end,
                recordId: record.id,
                record: record
            });
        }
    });
    timelineEvents.sort((a, b) => new Date(a.time) - new Date(b.time));

    const handleRequestDelete = (recordId, recordType) => {
        setDeleteTarget({ id: recordId, type: recordType });
    };

    const handleRequestDeleteRecord = (event) => {
        let recordId, recordType, message, deleteMode;

        if (event.type === 'Príchod') {
            recordId = event.record.id;
            recordType = 'attendance';
            if (event.record.end_time) {
                deleteMode = 'full_delete';
                message = 'Vymaže sa celá zmena vrátane odchodu, prestávok a statusov.';
            } else {
                deleteMode = 'full_delete';
                message = 'Vymaže sa záznam príchodu.';
            }
        } else if (event.type === 'Odchod') {
            recordId = event.record.id;
            recordType = 'attendance';
            deleteMode = 'clear_end';
            message = 'Odchod sa zruší a zmena zostane otvorená. Prestávky a statusy zostanú zachované.';
        } else if (event.type === 'Začiatok prestávky' || event.type === 'Koniec prestávky') {
            recordId = event.breakRecord.id;
            recordType = 'break';
            deleteMode = 'full_delete';
            message = 'Vymaže sa celý záznam prestávky.';
        } else if (event.type === 'Status Start' || event.type === 'Status End') {
            recordId = event.statusRecord.id;
            recordType = 'status';
            deleteMode = 'full_delete';
            message = 'Vymaže sa celý záznam statusu.';
        } else {
            return;
        }

        setDeleteTarget({ id: recordId, type: recordType, message, deleteMode, record: event.record });
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;

        const { id, type, deleteMode } = deleteTarget;
        let deletePromise;

        if (type === 'attendance' && deleteMode === 'clear_end') {
            deletePromise = attendanceService.updateAttendanceRecord(id, 'shift', {
                end_time: null,
                scan_type_end: null,
            });
        } else if (type === 'attendance') {
            deletePromise = attendanceService.deleteAttendanceRecord(id);
        } else if (type === 'break') {
            deletePromise = attendanceService.deleteBreakRecord(id);
        } else {
            setDeleteTarget(null);
            return;
        }

        try {
            await deletePromise;
            const historyRes = await attendanceService.getEmployeeHistory(employee.id);
            setHistory(historyRes.data || []);
            const successMessage =
                type === 'attendance' && deleteMode === 'clear_end'
                    ? 'Odchod bol úspešne zrušený.'
                    : 'Záznam úspešne vymazaný.';
            setSnackbar({ open: true, message: successMessage, severity: 'success' });
            if (onRecordUpdate) onRecordUpdate();
        } catch (error) {
            console.error(`Failed to delete ${type} record:`, error);
            const errorMessage = error.response?.data?.message || 'Nepodarilo sa vymazať záznam.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setDeleteTarget(null);
        }
    };

    const fetchWorkplaces = async () => {
        try {
            setWorkplacesLoading(true);
            const response = await workplaceService.getWorkplaces();
            setWorkplaces(response.data || []);
        } catch (error) {
            console.error("Failed to fetch workplaces:", error);
        } finally {
            setWorkplacesLoading(false);
        }
    };

    const fetchAssignedWorkplaces = async () => {
        if (!employee) return;
        try {
            const response = await employeeService.getAssignedWorkplaces(employee.id);
            setAssignedWorkplaces(new Set(response.data || []));
        } catch (error) {
            console.error("Failed to fetch assigned workplaces:", error);
        }
    };

    const handleWorkplaceToggle = (workplaceId) => {
        const newAssigned = new Set(assignedWorkplaces);
        if (newAssigned.has(workplaceId)) {
            newAssigned.delete(workplaceId);
        } else {
            newAssigned.add(workplaceId);
        }
        setAssignedWorkplaces(newAssigned);
    };

    const handleAddRecord = async (eventType, startTimeStr, endTimeStr, selectedWorkplaceId) => {
        if (!employee) return;

        try {
            const selectedDateStr = selectedDate.format('YYYY-MM-DD');
            const startDateTime = dayjs(`${selectedDateStr} ${startTimeStr}`).format('YYYY-MM-DD HH:mm:ss');
            const endDateTime = endTimeStr ? dayjs(`${selectedDateStr} ${endTimeStr}`).format('YYYY-MM-DD HH:mm:ss') : null;

            // Get first assigned workplace
            const workplaceIds = Array.from(assignedWorkplaces);
            if (workplaceIds.length === 0) {
                setSnackbar({ open: true, message: 'Zamestnanec nemá priradené žiadne pracovisko.', severity: 'error' });
                return;
            }
            const workplaceId = (selectedWorkplaceId && workplaceIds.includes(selectedWorkplaceId))
                ? selectedWorkplaceId
                : workplaceIds[0];

            switch (eventType) {
                case 'ARRIVAL': {
                    // Create attendance with start_time only
                    await attendanceService.createAttendanceRecord(employee.id, workplaceId, {
                        start_time: startDateTime,
                    });
                    break;
                }
                case 'DEPARTURE': {
                    // Check if there's an open attendance for this day
                    const openAttendance = selectedDayRecords.find(r => !r.end_time);
                    if (openAttendance) {
                        // Update existing attendance
                        await attendanceService.updateAttendanceRecord(openAttendance.id, 'shift', {
                            end_time: startDateTime,
                            scan_type_end: 'edit',
                        });
                    } else {
                        // Create new attendance with end_time only (edge case)
                        await attendanceService.createAttendanceRecord(employee.id, workplaceId, {
                            end_time: startDateTime,
                        });
                    }
                    break;
                }
                case 'FULL_SHIFT': {
                    // Create attendance with both start_time and end_time
                    await attendanceService.createAttendanceRecord(employee.id, workplaceId, {
                        start_time: startDateTime,
                        end_time: endDateTime,
                    });
                    break;
                }
                case 'BREAK_START': {
                    // Find attendance for this day (can be ended, that's OK)
                    let attendanceRecord = selectedDayRecords.find(r => 
                        dayjs(r.start_time).isSame(selectedDate, 'day')
                    );
                    
                    if (!attendanceRecord) {
                        // If no attendance exists, we need to create one
                        // Use the break start time as attendance start time if reasonable
                        const newAttendance = await attendanceService.createAttendanceRecord(employee.id, workplaceId, {
                            start_time: startDateTime,
                        });
                        attendanceRecord = { id: newAttendance.data.id };
                    }

                    // Create break with break_start_time
                    await attendanceService.createBreakRecord(attendanceRecord.id, {
                        break_start_time: startDateTime,
                    });
                    break;
                }
                case 'BREAK_END': {
                    // Find attendance for this day
                    let attendanceRecord = selectedDayRecords.find(r => 
                        dayjs(r.start_time).isSame(selectedDate, 'day')
                    );
                    
                    if (!attendanceRecord) {
                        setSnackbar({ 
                            open: true, 
                            message: 'Pre pridanie konca prestávky musí existovať záznam zmeny pre tento deň.', 
                            severity: 'error' 
                        });
                        return;
                    }

                    // Find open break and update it
                    const openBreak = selectedDayRecords
                        .flatMap(r => r.breaks || [])
                        .find(b => !b.break_end_time && b.attendance_id === attendanceRecord.id);

                    if (openBreak) {
                        await attendanceService.updateAttendanceRecord(openBreak.id, 'break', {
                            break_end_time: startDateTime,
                        });
                    } else {
                        setSnackbar({ 
                            open: true, 
                            message: 'Pre pridanie konca prestávky musí existovať otvorená prestávka. Najprv pridajte začiatok prestávky.', 
                            severity: 'error' 
                        });
                        return;
                    }
                    break;
                }
                case 'BREAK_DURATION': {
                    // Find attendance for this day (can be ended, that's OK)
                    let attendanceRecord = selectedDayRecords.find(r => 
                        dayjs(r.start_time).isSame(selectedDate, 'day')
                    );
                    
                    if (!attendanceRecord) {
                        // If no attendance exists, create one with start_time from break start
                        const newAttendance = await attendanceService.createAttendanceRecord(employee.id, workplaceId, {
                            start_time: startDateTime,
                        });
                        attendanceRecord = { id: newAttendance.data.id };
                    }

                    // Create break with both break_start_time and break_end_time
                    await attendanceService.createBreakRecord(attendanceRecord.id, {
                        break_start_time: startDateTime,
                        break_end_time: endDateTime,
                    });
                    break;
                }
                default:
                    setSnackbar({ open: true, message: 'Neznámy typ záznamu.', severity: 'error' });
                    return;
            }

            // Refresh history
            const historyRes = await attendanceService.getEmployeeHistory(employee.id);
            setHistory(historyRes.data || []);
            setSnackbar({ open: true, message: 'Záznam úspešne pridaný.', severity: 'success' });
            if (onRecordUpdate) onRecordUpdate();
        } catch (error) {
            console.error("Failed to add record:", error);
            const errorMessage = error.response?.data?.message || 'Nepodarilo sa pridať záznam.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    const drawerContent = (
        <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: '100vh' }}>
            {!employee ? (
                <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />
            ) : (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                            <IconButton onClick={onClose}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 50, height: 50, fontSize: '1.2rem' }}>
                                {getInitials(employee.name)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {employee.name}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'primary.main' }}>
                                    {employee.position || '—'}
                                </Typography>
                                {isNameDayToday(employee.name_day_month, employee.name_day_day) ? (
                                    <Chip
                                        label="Dnes má meniny"
                                        size="small"
                                        color="primary"
                                        sx={{ mt: 0.5 }}
                                    />
                                ) : null}
                            </Box>
                        </Stack>
                        {/* Šuplík je rýchly pohľad; celý príbeh človeka je na jeho stránke. */}
                        <IconButton
                            component={RouterLink}
                            to={`/employees/${employee.id}`}
                            aria-label={t('pages.employeeProfile.openPage')}
                            title={t('pages.employeeProfile.openPage')}
                            sx={{ color: 'grey.700' }}
                        >
                            <OpenInNewRounded />
                        </IconButton>
                        <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-controls={openMenu ? 'long-menu' : undefined}
                            aria-expanded={openMenu ? 'true' : undefined}
                            aria-haspopup="true"
                            onClick={handleMenuClick}
                            sx={{ color: 'grey.700' }}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Stack>

                    <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: 'text.secondary' }}>
                            Profilové údaje
                        </Typography>
                        <Stack spacing={1}>
                            <Typography variant="body2">
                                <strong>Meniny:</strong> {formatNameDayLabel(employee.name_day_month, employee.name_day_day)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Narodeniny:</strong> {formatProfileDate(employee.birthday)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Nástup do práce:</strong> {formatProfileDate(employee.employment_start_date)}
                            </Typography>
                        </Stack>
                    </Paper>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs
                            value={currentTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            aria-label="employee actions tabs"
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: 'grey.600',
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            <Tab label={t('pages.employeeDrawer.tabs.timeline')} value="timeline" />
                            <Tab label={t('pages.employeeDrawer.tabs.plan')} value="plan" />
                            <Tab label={t('pages.employeeDrawer.tabs.message')} value="notification" data-testid="notification-tab" />
                        </Tabs>
                    </Box>

                    {currentTab === 'timeline' && (
                        <Box sx={{ mb: 2 }}>
                            {/* Prepínač obdobia patrí sem, k číslam, ktoré mení.
                                V paneli nástrojov zoznamu vyzeral, že nerobí nič. */}
                            {onInsightsPeriodChange && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                    <SegmentedToggle
                                        value={insightsPeriod}
                                        onChange={onInsightsPeriodChange}
                                        options={[
                                            { value: 30, label: t('pages.employees.insights.period30') },
                                            { value: 90, label: t('pages.employees.insights.period90') },
                                            { value: 365, label: t('pages.employees.insights.period365') },
                                        ]}
                                        size="sm"
                                    />
                                </Box>
                            )}
                            <EmployeeInsightsDetail
                                insight={insightDetail}
                                loading={insightLoading}
                                periodLabel={insightsPeriodLabel}
                                t={t}
                            />
                        </Box>
                    )}

                    {/* Date Picker - show for timeline and plan tabs */}
                    {(currentTab === 'timeline' || currentTab === 'plan') && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                                {t('pages.employeeDrawer.selectedDate')}
                            </Typography>
                            <StaticDatePicker
                                displayStaticWrapperAs="desktop"
                                value={selectedDate}
                                onChange={handleDateChange}
                                slotProps={{
                                    actionBar: {
                                        actions: [],
                                    },
                                }}
                                slots={{
                                    day: (props) => {
                                        const hasEvent = historyDays.includes(props.day.format('YYYY-MM-DD'));
                                        const daySx = {
                                            '&.Mui-selected': {
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                '&:hover, &:focus': {
                                                    backgroundColor: theme.palette.primary.dark,
                                                },
                                            },
                                            '&.MuiPickersDay-today': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                            borderRadius: '8px',
                                        };

                                        if (hasEvent && !props.selected) {
                                            daySx.border = `1px solid ${theme.palette.primary.main}`;
                                            daySx.backgroundColor = 'transparent';
                                        }

                                        return (
                                            <PickersDay {...props} sx={daySx} />
                                        );
                                    },
                                }}
                                sx={{
                                    '& .MuiDateCalendar-root': {
                                        width: '100%',
                                        maxWidth: '280px',
                                    },
                                    '& .MuiPickersCalendarHeader-root': {
                                        padding: '8px 0',
                                    },
                                    '& .MuiPickersCalendarHeader-label': {
                                        fontSize: '0.875rem',
                                    },
                                    '& .MuiDayCalendar-weekDayLabel': {
                                        fontSize: '0.75rem',
                                        padding: '4px 0',
                                    },
                                    '& .MuiPickersDay-root': {
                                        width: '32px',
                                        height: '32px',
                                        fontSize: '0.75rem',
                                    },
                                    '& .MuiDateCalendar-viewTransitionContainer': {
                                        paddingBottom: '8px',
                                    },
                                }}
                            />
                        </Box>
                    )}

                    <Menu
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleMenuClose}
                        keepMounted={false}
                        disableRestoreFocus={true}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                borderRadius: '16px',
                                p: 1,
                                '& .MuiAvatar-root': {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            },
                        }}
                    >
                        <MenuItem onClick={handleOpenEditEmployeeModal}>
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            Edit Employee
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { setDeleteDialogOpen(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                            </ListItemIcon>
                            Delete Employee
                        </MenuItem>
                    </Menu>

                    {currentTab === 'timeline' && (
                        <Box>
                            {loading ? (
                                <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />
                            ) : (
                                <>
                                    <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {activeBreak && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<FreeBreakfastIcon />}
                                                    onClick={handleEndActiveBreak}
                                                    sx={{
                                                        bgcolor: 'warning.main',
                                                        '&:hover': {
                                                            bgcolor: 'warning.dark',
                                                        },
                                                        borderRadius: '12px',
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    Ukončiť prestávku
                                                </Button>
                                            )}
                                            {activeRecord && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<LogoutIcon />}
                                                    onClick={handleEndShift}
                                                    sx={{
                                                        bgcolor: 'error.main',
                                                        '&:hover': {
                                                            bgcolor: 'error.dark',
                                                        },
                                                        borderRadius: '12px',
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    Ukončiť zmenu
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => setIsAddDialogOpen(true)}
                                                sx={{
                                                    bgcolor: theme.palette.mode === 'dark' ? '#B4F481' : theme.palette.primary.main,
                                                    color: theme.palette.mode === 'dark' ? '#0f172a' : 'white',
                                                    '&:hover': {
                                                        bgcolor: theme.palette.mode === 'dark' ? '#a3e66e' : theme.palette.primary.dark,
                                                    },
                                                    borderRadius: '9999px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    px: 2,
                                                    py: 0.75,
                                                    fontSize: '0.75rem',
                                                    letterSpacing: '0.05em',
                                                    boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(180, 244, 129, 0.1)' : 'none',
                                                }}
                                            >
                                                Pridať
                                            </Button>
                                        </Stack>
                                    </Stack>

                                    {selectedDayRecords.length > 0 ? (
                                        <Paper sx={{ p: 3, borderRadius: 4, bgcolor: 'background.paper', boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)' }}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                            {activeStatus && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<WorkHistoryIcon />}
                                                    onClick={handleEndActiveStatus}
                                                    sx={{
                                                        bgcolor: 'info.main',
                                                        '&:hover': {
                                                            bgcolor: 'info.dark',
                                                        },
                                                        borderRadius: '12px',
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    Ukončiť status
                                                </Button>
                                            )}
                                        </Stack>


                                    {selectedDayRecords.map(record => (
                                        record.note ? (
                                            <Box key={`note-${record.id}`} sx={{ mb: 2, p: 2, bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.secondary : theme.palette.grey[50], borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        Note for shift {format(new Date(record.start_time), 'HH:mm')} - {record.end_time ? format(new Date(record.end_time), 'HH:mm') : 'Ongoing'}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>{record.note}</Typography>
                                            </Box>
                                        ) : null
                                    ))}

                                    <Box sx={{ position: 'relative', pl: 2 }}>
                                        {timelineEvents.map((event, index) => {
                                            const isLast = index === timelineEvents.length - 1;
                                            const getEventConfig = () => {
                                                switch (event.type) {
                                                    case 'Príchod':
                                                        return {
                                                            label: 'Príchod',
                                                            subLabel: 'Začiatok zmeny',
                                                            bgColor: theme.palette.mode === 'dark' ? '#B4F481' : '#4CAF50',
                                                            textColor: theme.palette.mode === 'dark' ? '#0f172a' : '#fff',
                                                            icon: <DirectionsRunIcon sx={{ fontSize: 18 }} />
                                                        };
                                                    case 'Odchod':
                                                        return {
                                                            label: 'Odchod',
                                                            subLabel: 'Koniec zmeny',
                                                            bgColor: theme.palette.mode === 'dark' ? '#fb7185' : '#F44336',
                                                            textColor: theme.palette.mode === 'dark' ? '#0f172a' : '#fff',
                                                            icon: <LogoutIcon sx={{ fontSize: 18 }} />
                                                        };
                                                    case 'Začiatok prestávky':
                                                        return {
                                                            label: 'Začiatok prestávky',
                                                            subLabel: 'Pauza',
                                                            bgColor: theme.palette.mode === 'dark' ? '#fbbf24' : '#FFC107',
                                                            textColor: theme.palette.mode === 'dark' ? '#0f172a' : '#fff',
                                                            icon: <FreeBreakfastIcon sx={{ fontSize: 18 }} />
                                                        };
                                                    case 'Koniec prestávky':
                                                        return {
                                                            label: 'Koniec prestávky',
                                                            subLabel: 'Pokračovanie',
                                                            bgColor: theme.palette.mode === 'dark' ? '#fbbf24' : '#FFC107',
                                                            textColor: theme.palette.mode === 'dark' ? '#0f172a' : '#fff',
                                                            icon: <FreeBreakfastIcon sx={{ fontSize: 18 }} />
                                                        };
                                                    case 'Status Start':
                                                    case 'Status End':
                                                        return {
                                                            label: event.type,
                                                            subLabel: event.name || '',
                                                            bgColor: theme.palette.mode === 'dark' ? '#a78bfa' : '#9C27B0',
                                                            textColor: theme.palette.mode === 'dark' ? '#0f172a' : '#fff',
                                                            icon: <WorkHistoryIcon sx={{ fontSize: 18 }} />
                                                        };
                                                    default:
                                                        return {
                                                            label: event.type,
                                                            subLabel: '',
                                                            bgColor: theme.palette.mode === 'dark' ? '#64748b' : '#757575',
                                                            textColor: theme.palette.mode === 'dark' ? '#0f172a' : '#fff',
                                                            icon: <EditIcon sx={{ fontSize: 18 }} />
                                                        };
                                                }
                                            };
                                            const config = getEventConfig();
                                            
                                            return (
                                                <Box key={event.id} sx={{ position: 'relative', display: 'flex', gap: 2, mb: 2 }}>
                                                    {/* Timeline Line */}
                                                    {!isLast && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                left: '19px',
                                                                top: '40px',
                                                                bottom: '-16px',
                                                                width: '2px',
                                                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                                                borderRadius: '999px'
                                                            }}
                                                        />
                                                    )}
                                                    
                                                    {/* Icon Node */}
                                                    <Box
                                                        sx={{
                                                            position: 'relative',
                                                            zIndex: 10,
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: 4,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: config.bgColor,
                                                            color: config.textColor,
                                                            flexShrink: 0,
                                                            boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.15)'
                                                        }}
                                                    >
                                                        {config.icon}
                                                    </Box>
                                                    
                                                    {/* Content Card */}
                                                    <Box
                                                        onClick={(e) => {
                                                            // Don't open edit if clicking on delete icon
                                                            if (!e.target.closest('.delete-icon')) {
                                                                handleOpenEdit(event);
                                                            }
                                                        }}
                                                        sx={{
                                                            flex: 1,
                                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                                            border: `1px solid ${theme.palette.divider}`,
                                                            borderRadius: 4,
                                                            p: 2,
                                                            mb: 1,
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.2s',
                                                            '&:hover': {
                                                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                                            },
                                                            '& .edit-icon': {
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s',
                                                            },
                                                            '&:hover .edit-icon': {
                                                                opacity: 1,
                                                            }
                                                        }}
                                                    >
                                                        <Box>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                                                    {config.label}
                                                                </Typography>
                                                                {event.record && event.record.workplace_name && (event.type === 'Príchod' || event.type === 'Odchod') && (
                                                                    <Chip
                                                                        label={event.record.workplace_name}
                                                                        size="small"
                                                                        sx={{
                                                                            height: 20,
                                                                            fontSize: '0.625rem',
                                                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)',
                                                                            color: 'text.secondary',
                                                                            border: `1px solid ${theme.palette.divider}`,
                                                                            '& .MuiChip-label': {
                                                                                px: 1
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            </Stack>
                                                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.875rem' }}>
                                                                {config.subLabel}
                                                            </Typography>
                                                        </Box>
                                                        
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <Typography variant="h6" sx={{ 
                                                                fontWeight: 600, 
                                                                letterSpacing: '0.05em',
                                                                color: 'text.primary'
                                                            }}>
                                                                {format(new Date(event.time), 'HH:mm')}
                                                            </Typography>
                                                            {event.scan_type && (event.type === 'Príchod' || event.type === 'Odchod') && (
                                                                event.scan_type === 'nfc' ? (
                                                                    <NfcIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                ) : (
                                                                    <QrCodeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                )
                                                            )}
                                                            <Box
                                                                className="edit-icon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenEdit(event);
                                                                }}
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.12)',
                                                                    }
                                                                }}
                                                            >
                                                                <EditIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            </Box>
                                                            <Box
                                                                className="edit-icon delete-icon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRequestDeleteRecord(event);
                                                                }}
                                                                sx={{
                                                                    width: 32,
                                                                    height: 32,
                                                                    borderRadius: '50%',
                                                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.3)' : 'rgba(211, 47, 47, 0.15)',
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                    
                                                    {/* Edit Collapse */}
                                                    <Collapse 
                                                        in={editingEventId === event.id} 
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            left: 0, 
                                                            right: 0, 
                                                            top: '100%', 
                                                            zIndex: 20, 
                                                            bgcolor: 'background.paper', 
                                                            borderRadius: 2, 
                                                            mt: 1, 
                                                            p: 2, 
                                                            boxShadow: 3,
                                                            border: `1px solid ${theme.palette.divider}`
                                                        }}
                                                    >
                                                        <Box onClick={(e) => e.stopPropagation()}>
                                                            <DateTimePicker
                                                                value={editingTime}
                                                                onChange={setEditingTime}
                                                                slotProps={{
                                                                    textField: {
                                                                        fullWidth: true,
                                                                        size: 'small',
                                                                        sx: modalTextFieldStyle
                                                                    }
                                                                }}
                                                                ampm={false}
                                                            />
                                                            {(event.type === 'Príchod' || event.type === 'Odchod') && assignedWorkplaceOptions.length > 1 && (
                                                                <Select
                                                                    value={assignedWorkplaceOptions.some(wp => wp.id === editingWorkplaceId) ? editingWorkplaceId : ''}
                                                                    onChange={(e) => setEditingWorkplaceId(e.target.value)}
                                                                    displayEmpty
                                                                    fullWidth
                                                                    size="small"
                                                                    sx={{ mt: 1 }}
                                                                >
                                                                    <MenuItem value="" disabled>Pracovisko</MenuItem>
                                                                    {assignedWorkplaceOptions.map((wp) => (
                                                                        <MenuItem key={wp.id} value={wp.id}>{wp.name}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            )}
                                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                                <Button onClick={handleEditAttendanceSubmit} variant="contained" size="small" sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>Uložiť</Button>
                                                                <Button onClick={handleCancelEdit} size="small">Zrušiť</Button>
                                                            </Stack>
                                                            {editError && <Alert severity="error" sx={{ mt: 1 }}>{editError}</Alert>}
                                                        </Box>
                                                    </Collapse>
                                                </Box>
                                            );
                                        })}
                                    </Box>

                                    <Box sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(3, 1fr)', 
                                        gap: 2, 
                                        mt: 3 
                                    }}>
                                        <Box sx={{ 
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                            borderRadius: 4,
                                            p: 2,
                                            textAlign: 'center'
                                        }}>
                                            <Typography variant="caption" sx={{ 
                                                color: 'text.secondary',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.1em',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}>
                                                Total
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {activeRecord ? 'In Progress' : formatDuration(totalBruttoDurationMs)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ 
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                            borderRadius: 4,
                                            p: 2,
                                            textAlign: 'center',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: 2,
                                                background: 'linear-gradient(to right, #10b981, #059669)'
                                            }} />
                                            <Typography variant="caption" sx={{ 
                                                color: theme.palette.mode === 'dark' ? '#10b981' : theme.palette.success.main,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.1em',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}>
                                                Net Work
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {activeRecord ? 'In Progress' : formatDuration(totalWorkDurationMs)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ 
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                            borderRadius: 4,
                                            p: 2,
                                            textAlign: 'center'
                                        }}>
                                            <Typography variant="caption" sx={{ 
                                                color: theme.palette.mode === 'dark' ? '#fbbf24' : theme.palette.warning.main,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.1em',
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.5
                                            }}>
                                                Breaks
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {formatDuration(totalBreakDurationMs)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            ) : (
                                <Paper sx={{ textAlign: 'center', p: 4, color: 'text.secondary', borderRadius: 4, bgcolor: 'background.paper', boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)', border: `2px dashed ${theme.palette.divider}` }}>
                                    <Typography sx={{ mb: 1 }}>No records for this day.</Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Kliknite na Pridať pre vytvorenie záznamu.</Typography>
                                </Paper>
                            )}
                                </>
                            )}
                        </Box>
                    )}

                    {currentTab === 'plan' && (
                        <Paper sx={{ p: 3, borderRadius: 4, bgcolor: 'background.paper', boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)' }}>
                            {dayPlanLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
                                    <CircularProgress size={24} />
                                    <Typography variant="body2" color="text.secondary">{t('pages.employeeDrawer.dayPlan.loading')}</Typography>
                                </Box>
                            ) : dayPlan && dayPlan.blocks && dayPlan.blocks.length > 0 ? (
                                <Box>
                                    {dayPlan.blocks.map((block) => (
                                        <Box key={block.id} sx={{ mb: 3 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                                                {block.title}
                                            </Typography>
                                            <Stack spacing={1}>
                                                {(block.tasks || []).map((task) => (
                                                    <Box key={task.id}>
                                                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                            <Checkbox
                                                                checked={!!task.completed}
                                                                disabled
                                                                size="small"
                                                                sx={{ mt: -0.5, p: 0.5 }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    flex: 1,
                                                                    textDecoration: task.completed ? 'line-through' : 'none',
                                                                    color: task.completed ? 'text.secondary' : 'text.primary',
                                                                }}
                                                            >
                                                                {task.text}
                                                            </Typography>
                                                        </Stack>
                                                        {task.note && (
                                                            <Typography variant="caption" sx={{ display: 'block', ml: 4, mt: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                                                                {task.note}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                    {t('pages.employeeDrawer.dayPlan.noPlan')}
                                </Typography>
                            )}
                        </Paper>
                    )}

                    <Dialog
                        open={!!deleteTarget}
                        onClose={() => setDeleteTarget(null)}
                        aria-labelledby="delete-confirmation-title"
                        PaperProps={{ sx: { borderRadius: '16px' } }}
                    >
                        <DialogTitle id="delete-confirmation-title" sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                            Naozaj chcete vymazať záznam?
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {deleteTarget?.message || 'Táto akcia je trvalá a nedá sa vrátiť späť. Všetky súvisiace údaje budú vymazané.'}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ p: '16px 24px' }}>
                            <Button onClick={() => setDeleteTarget(null)} sx={{ color: 'text.secondary', borderRadius: '12px' }}>
                                Zrušiť
                            </Button>
                            <Button 
                                onClick={handleConfirmDelete} 
                                color="error" 
                                variant="contained"
                                sx={{ borderRadius: '12px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                            >
                                Vymazať
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {currentTab === 'notification' && (
                        <Paper sx={{ p: 3, borderRadius: 4, bgcolor: 'background.paper', boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)' }}>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                        Title
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {["Admin", "URGENT", "INFO", "REMINDER", "UPDATE", "SYSTEM"].map((option) => (
                                            <Chip
                                                key={option}
                                                label={option}
                                                onClick={() => setNotificationData({ ...notificationData, title: option })}
                                                variant={notificationData.title === option ? "filled" : "outlined"}
                                                sx={{
                                                    bgcolor: notificationData.title === option ? theme.palette.primary.main : 'transparent',
                                                    color: notificationData.title === option ? theme.palette.primary.contrastText : 'grey.700',
                                                    borderColor: notificationData.title === option ? theme.palette.primary.main : 'grey.300',
                                                    '&:hover': {
                                                        bgcolor: notificationData.title === option ? theme.palette.primary.dark : 'grey.100',
                                                    },
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                                <TextField
                                    label="Message"
                                    value={notificationData.body}
                                    onChange={(e) => setNotificationData({ ...notificationData, body: e.target.value })}
                                    fullWidth
                                    multiline
                                    rows={4}
                                    sx={modalTextFieldStyle}
                                />
                                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                    This message will be sent as a push notification to the employee's device.
                                </Alert>
                                {!employee?.has_fcm_token && (
                                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                                        This employee has not enabled notifications in the app. The notification cannot be sent.
                                    </Alert>
                                )}
                                <Box sx={{ pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        onClick={handleSendNotification}
                                        variant="contained"
                                        startIcon={<SendIcon />}
                                        disabled={!notificationData.body.trim() || !employee?.has_fcm_token}
                                        sx={{
                                            bgcolor: (notificationData.body.trim() && employee?.has_fcm_token) ? theme.palette.primary.main : 'grey.300',
                                            color: (notificationData.body.trim() && employee?.has_fcm_token) ? theme.palette.primary.contrastText : 'white',
                                            borderRadius: '12px',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: (notificationData.body.trim() && employee?.has_fcm_token) ? theme.palette.primary.dark : 'grey.300',
                                                boxShadow: 'none'
                                            },
                                            '&:disabled': {
                                                bgcolor: 'grey.300',
                                                color: 'grey.500'
                                            }
                                        }}
                                    >
                                        Send
                                    </Button>
                                </Box>
                            </Stack>
                        </Paper>
                    )}
                </>
            )}



            {editingEmployeeData && (
                <Dialog
                    open={isEditEmployeeModalOpen}
                    onClose={handleCloseEditEmployeeModal}
                    fullWidth
                    maxWidth="sm"
                    keepMounted={false}
                    disableRestoreFocus={true}
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>Edit Employee</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField label="Name" name="name" value={editingEmployeeData.name} onChange={(e) => setEditingEmployeeData({ ...editingEmployeeData, name: e.target.value })} fullWidth sx={modalTextFieldStyle} />
                            <TextField label="Position" name="position" value={editingEmployeeData.position} onChange={(e) => setEditingEmployeeData({ ...editingEmployeeData, position: e.target.value })} fullWidth sx={modalTextFieldStyle} />
                            <TextField label="Email" name="email" value={editingEmployeeData.email} onChange={(e) => setEditingEmployeeData({ ...editingEmployeeData, email: e.target.value })} fullWidth sx={modalTextFieldStyle} />
                            <TextField label="Phone Number" name="phone_number" value={editingEmployeeData.phone_number} onChange={(e) => setEditingEmployeeData({ ...editingEmployeeData, phone_number: e.target.value })} fullWidth sx={modalTextFieldStyle} />

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Meniny"
                                    value={
                                        editingEmployeeData.name_day_month && editingEmployeeData.name_day_day
                                            ? dayjs('2000-01-01')
                                                .month(editingEmployeeData.name_day_month - 1)
                                                .date(editingEmployeeData.name_day_day)
                                            : null
                                    }
                                    onChange={(value) => {
                                        setEditingEmployeeData({
                                            ...editingEmployeeData,
                                            name_day_month: value ? value.month() + 1 : null,
                                            name_day_day: value ? value.date() : null,
                                        });
                                        if (!value) {
                                            setNameDayTouched(true);
                                        }
                                    }}
                                    onAccept={() => setNameDayTouched(true)}
                                    closeOnSelect={false}
                                    referenceDate={dayjs('2000-01-01')}
                                    views={['month', 'day']}
                                    format="D. M."
                                    slotProps={{
                                        textField: { fullWidth: true, sx: modalTextFieldStyle },
                                        actionBar: { actions: ['clear', 'cancel', 'accept'] },
                                    }}
                                />
                                <DatePicker
                                    label="Narodeniny"
                                    value={editingEmployeeData.birthday ? dayjs(editingEmployeeData.birthday) : null}
                                    onChange={(value) => {
                                        setEditingEmployeeData({
                                            ...editingEmployeeData,
                                            birthday: value ? value.format('YYYY-MM-DD') : null,
                                        });
                                    }}
                                    format="D. M. YYYY"
                                    slotProps={{ textField: { fullWidth: true, sx: modalTextFieldStyle } }}
                                />
                                <DatePicker
                                    label="Nástup do práce"
                                    value={editingEmployeeData.employment_start_date ? dayjs(editingEmployeeData.employment_start_date) : null}
                                    onChange={(value) => {
                                        setEditingEmployeeData({
                                            ...editingEmployeeData,
                                            employment_start_date: value ? value.format('YYYY-MM-DD') : null,
                                        });
                                    }}
                                    format="D. M. YYYY"
                                    slotProps={{ textField: { fullWidth: true, sx: modalTextFieldStyle } }}
                                />
                            </LocalizationProvider>
                            
                            <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                                    Assign to Workplaces
                                </Typography>
                                {workplacesLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <FormGroup>
                                        {workplaces.map(wp => (
                                            <FormControlLabel
                                                key={wp.id}
                                                control={
                                                    <Checkbox
                                                        checked={assignedWorkplaces.has(wp.id)}
                                                        onChange={() => handleWorkplaceToggle(wp.id)}
                                                        sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }}
                                                    />
                                                }
                                                label={wp.name}
                                            />
                                        ))}
                                    </FormGroup>
                                )}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: '16px 24px' }}>
                        <Button onClick={handleCloseEditEmployeeModal} sx={{ color: 'grey.700', borderRadius: '12px' }}>Cancel</Button>
                        <Button onClick={handleEditEmployeeSubmit} variant="contained" sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '12px', boxShadow: 'none', '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' } }}>Save Changes</Button>
                    </DialogActions>
                </Dialog>
            )}

            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                keepMounted={false}
                disableRestoreFocus={true}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                    {"Are you sure you want to delete this employee?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        This action is permanent and cannot be undone. All data associated with this employee will be deleted.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'grey.700', borderRadius: '12px' }}>Cancel</Button>
                    <Button onClick={handleDeleteEmployee} autoFocus color="error" variant="contained" sx={{ borderRadius: '12px', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <AddEditRecordDialog
                open={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                onSave={handleAddRecord}
                selectedDate={selectedDate}
                hasActiveRecord={!!activeRecord}
                hasActiveBreak={!!activeBreak}
                hasAnyRecords={selectedDayRecords.length > 0}
                onShowToast={(message, severity) => setSnackbar({ open: true, message, severity: severity || 'warning' })}
                workplaceOptions={assignedWorkplaceOptions}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                keepMounted={false}
                disableRestoreFocus={true}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                keepMounted={false}
                disableRestoreFocus={true}
                PaperProps={{
                    sx: {
                        width: { xs: '100vw', sm: 500, md: 600 },
                        backgroundColor: theme.palette.background.default,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        transition: 'background-color 0.3s ease, border-color 0.3s ease',
                    }
                }}
            >
                {drawerContent}
            </Drawer>
        </LocalizationProvider>
    );
};

export default EmployeeHistoryDrawer; 